// Import/upload archive. Every uploaded file is:
//   1. archived to Storage  users/{uid}/profiles/{pid}/imports/{ts}_{file}
//   2. logged as an IMMUTABLE record  users/{uid}/profiles/{pid}/imports/{id}
//      with a SHA-256 hash for integrity/provenance.
// Rules forbid update/delete, so the archive + log are tamper-evident.
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { fbDb, fbStorage, fbAuth } from './firebase';

export interface ImportRecord {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  sha256: string;
  kind: string;        // ccda | dicom | pdf | csv | excel | other
  storagePath: string;
  uploadedBy: string;
  status: string;      // 'archived'
  createdAt?: { seconds: number } | null;
}

export function kindOf(name: string, mime: string): string {
  const n = (name || '').toLowerCase();
  if (/\.(xml|zip|ccda|ccd|cda)$/.test(n)) return 'ccda';
  if (/\.dcm$/.test(n) || mime === 'application/dicom') return 'dicom';
  if (/\.pdf$/.test(n) || mime === 'application/pdf') return 'pdf';
  if (/\.csv$/.test(n)) return 'csv';
  if (/\.(xlsx|xls)$/.test(n)) return 'excel';
  return 'other';
}

async function sha256(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

const importsCol = (uid: string, pid: string) => collection(fbDb, 'users', uid, 'profiles', pid, 'imports');

export async function uploadImport(ownerUid: string, pid: string, file: File, uploadedBy: string): Promise<string> {
  const hash = await sha256(file);
  const safe = (file.name || 'file').replace(/[^\w.\-]+/g, '_');
  const path = `users/${ownerUid}/profiles/${pid}/imports/${Date.now()}_${safe}`;
  await uploadBytes(ref(fbStorage, path), file, { contentType: file.type || 'application/octet-stream' });
  // Stamp creator provenance (plaintext) — firestore.rules require createdByUid ==
  // the writer's uid on every record create (imports included), same as addRecord().
  const u = fbAuth.currentUser;
  const doc = await addDoc(importsCol(ownerUid, pid), {
    filename: file.name, mimeType: file.type || '', size: file.size, sha256: hash,
    kind: kindOf(file.name, file.type || ''), storagePath: path, uploadedBy, status: 'archived',
    createdByUid: u?.uid ?? null,
    createdByEmail: (u?.email ?? '').toLowerCase(),
    createdByName: u?.displayName ?? '',
    createdAt: serverTimestamp(),
  });
  return doc.id;
}

export async function listImports(ownerUid: string, pid: string): Promise<ImportRecord[]> {
  const snap = await getDocs(importsCol(ownerUid, pid));
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }) as ImportRecord)
    .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
}

// Report/import files are served through the backend (mpwrd-api /api/report-file),
// NOT fetched directly from Storage: a co-parent (guardian) or provider (doctor /
// physician / nurse) is authorized server-side against the Firestore access model
// (Storage rules can't read Firestore reliably), then the file is streamed back
// read-only. Returns a short-lived in-memory object URL for the viewer to open.
export async function importDownloadURL(storagePath: string): Promise<string> {
  const token = fbAuth.currentUser ? await fbAuth.currentUser.getIdToken() : '';
  const res = await fetch('/api/report-file', {
    method: 'POST',
    headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ path: storagePath }),
  });
  if (!res.ok) throw new Error(`Could not open report (${res.status})`);
  return URL.createObjectURL(await res.blob());
}
